import React from 'react';
import { Form as AntdForm, FormInstance, Typography } from 'antd';
import { FormItemProps as AntdFormItemProps } from 'antd/lib/form';
import styled from 'styled-components';
import cx from 'classnames';
import { toPath } from 'lodash';

import theme from '~/constants/theme';
import colors from '~/constants/colors';
import { useFormItemContext } from './FormItemContext';
import { useSoftDeclineItemContext } from './useSoftDeclineItemContext';
import FloatLabel from './FloatLabel';
import { InputContextProvider } from './InputContext';
import { useTranslation } from 'react-i18next';
import { useDirection } from '~/hooks/i18nHooks';
import { SaudiReyalSymbolIcon } from '~/img';

export interface FormItemProps<Values> extends AntdFormItemProps<Values> {
  placeholder?: any;
  label?: string;
  optional?: boolean;
  children?: any;
  disabled?: boolean;
  textarea?: boolean;
  forceDisabled?: boolean;
  labelText?: string;
}

type FormItemType<Values = any> = (props: FormItemProps<Values>) => React.ReactElement;

/** Replace {SAR} token in a label string with the Saudi Riyal SVG icon */
function renderLabelWithSarIcon(label: string): React.ReactNode {
  if (!label.includes('{SAR}')) return label;
  const parts = label.split('{SAR}');
  return (
    <>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <SaudiReyalSymbolIcon
              className="sar-icon"
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
}

function FormItem<Values = any>(props: FormItemProps<Values>): React.ReactElement {
  const {
    children,
    label,
    optional,
    rules = [],
    shouldUpdate,
    noStyle,
    disabled,
    className,
    textarea,
    forceDisabled,
    labelText = '',
    ...rest
  } = props;
  const { name } = rest;
  const formItemContext = useFormItemContext();
  const dir = useDirection();
  const softDeclineContext = useSoftDeclineItemContext(name);
  const { t } = useTranslation('ksaApplication');
  const contextOptional = !!Object.keys(formItemContext.optionalFields || {}).find(
    (optionalNamePath) => {
      if (!name) return false;
      const resultOptionalNamePath = toPath(optionalNamePath).join('.');
      const resultItemNamePath = toPath([name].flat()).join('.');
      if (resultOptionalNamePath === resultItemNamePath) {
        return true;
      }
      return false;
    }
  );
  const resultOptional = optional || contextOptional;

  const hasSoftDeclineWarning = !forceDisabled && softDeclineContext.withWarning;

  const resultClassName = cx(className, {
    FormItem__withWarning: hasSoftDeclineWarning,
  });
  const resultDisabled =
    forceDisabled ||
    formItemContext.disabled ||
    (!softDeclineContext.editableBySoftDecline &&
      (formItemContext.disabledByDefault || softDeclineContext.disabled || disabled));
  const resultExtra = hasSoftDeclineWarning ? softDeclineContext.warningMessage : undefined;

  const defaultRule: typeof rules = formItemContext.defaultValidator
    ? [{ validator: formItemContext.defaultValidator }]
    : [];

  if (shouldUpdate) {
    return (
      <InputContextProvider value={{ optional: resultOptional }}>
        <AntdForm.Item shouldUpdate={shouldUpdate} noStyle>
          {(form) => (
            <AntdForm.Item
              rules={defaultRule.concat(rules)}
              noStyle={noStyle}
              extra={resultExtra}
              className={resultClassName}
              {...rest}
            >
              {typeof children === 'function' ? children?.(form) : children}
            </AntdForm.Item>
          )}
        </AntdForm.Item>
      </InputContextProvider>
    );
  }
  if (noStyle) {
    return (
      <InputContextProvider value={{ optional: resultOptional }}>
        <AntdForm.Item
          rules={defaultRule.concat(rules)}
          noStyle={noStyle}
          className={resultClassName}
          extra={resultExtra}
          {...rest}
        >
          {children}
        </AntdForm.Item>
      </InputContextProvider>
    );
  }
  return (
    <InputContextProvider value={{ optional: resultOptional }}>
      <Container dir={dir}>
        {!rest.hidden && (
          <div style={{ display: 'flex', marginBottom: -8, alignItems: 'center' }}>
            <Typography.Paragraph className="labelTextInput">
              {renderLabelWithSarIcon(t(labelText))}
              {optional && (
                <span className="label-optional">
                  {` ${t('(Optional)')}`}
                </span>
              )}
            </Typography.Paragraph>
          </div>
        )}
        <AntdForm.Item
          rules={defaultRule.concat(rules)}
          noStyle={noStyle}
          className={resultClassName}
          extra={resultExtra}
          {...rest}
        >
          <FloatLabel
            label={label}
            optional={resultOptional}
            disabled={resultDisabled}
            textarea={textarea}
          >
            {children}
          </FloatLabel>
        </AntdForm.Item>
      </Container>
    </InputContextProvider>
  );
}
const Container = styled.div<any>`
  .labelTextInput {
    color: #070707;
    //margin-bottom: 0;
    /* FontSize (Body)/Medium */
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
  }
  .label-optional {
    color: #070707;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    letter-spacing: 0.22px;
  }
  .sar-icon {
    display: inline-block;
    vertical-align: middle;
    margin: 0 1px;
  }
`;

export type RenderChildren<Values = any> = (form: FormInstance<Values>) => React.ReactNode;

export default styled(FormItem)`
  .ant-form-item-explain {
    font-size: 12px;
    min-height: ${theme.formVerticalSpacing}px !important;
    transition: 0.2s ease-in color;
    &:not(
        .ant-form-item-explain-error,
        .ant-form-item-explain-warning,
        .ant-form-item-explain-success
      ) {
      color: ${colors.textColor};
    }
  }
  &.ant-form-item-has-warning {
    .ant-upload-select-picture-card {
      border-color: ${colors.warningColor} !important;
    }
    .ant-upload-list-picture-card .ant-upload-list-item {
      border: 1px solid ${colors.warningColor};
    }
  }
  &:not(.ant-form-item-has-error, .FormItem__withWarning) {
    .ant-input:focus,
    .ant-input-number:focus,
    .ant-input-number-focused,
    .ant-picker-focused {
      border-color: ${colors.inputHoverBorderColor};
      box-shadow: 0 0 0 2px ${colors.inputHoverBorderColor};
    }
    .ant-select-focused .ant-select-selector {
      border-color: ${colors.inputHoverBorderColor} !important;
      box-shadow: 0 0 0 2px ${colors.inputHoverBorderColor} !important;
    }
  }
  &.FormItem__withWarning {
    // Regular input
    .ant-input:focus,
    .ant-input-number:focus,
    .ant-input-number-focused,
    .ant-picker-focused {
      border-color: ${colors.warningColor};
      box-shadow: 0 0 0 1px ${colors.warningColor};
    }
    .ant-radio-group:after,
    .ant-checkbox-wrapper-in-form-item:after {
      content: '';
      background: url('data:image/svg+xml;utf8,
      <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z" fill="${encodeURIComponent(
  colors.warningColor
)}" stroke="${encodeURIComponent(colors.warningColor)}" stroke-width="1.2"/>
        <path d="M7.00001 11.9319C7.00089 12.1797 7.0876 12.4195 7.24539 12.6106C7.40317 12.8017 7.62229 12.9322 7.86545 12.9799C8.10862 13.0277 8.36081 12.9897 8.57912 12.8724C8.79744 12.7552 8.96838 12.5659 9.06288 12.3368C9.15738 12.1077 9.16958 11.853 9.09743 11.6159C9.02527 11.3789 8.87321 11.1741 8.66711 11.0365C8.46101 10.8989 8.2136 10.837 7.96699 10.8613C7.72037 10.8855 7.48978 10.9945 7.31444 11.1696C7.21431 11.2696 7.13496 11.3885 7.081 11.5193C7.02703 11.6501 6.9995 11.7903 7.00001 11.9319ZM7.5355 9.43068H8.60744C8.65478 9.43068 8.7002 9.41194 8.73377 9.37855C8.76734 9.34516 8.78632 9.29985 8.78657 9.2525V3.17818C8.78632 3.131 8.76747 3.08583 8.73411 3.05246C8.70075 3.0191 8.65557 3.00025 8.60839 3H7.5355C7.48816 3.00025 7.44284 3.01924 7.40945 3.0528C7.37606 3.08637 7.35732 3.13179 7.35732 3.17913V9.25345C7.35782 9.30047 7.37678 9.3454 7.41012 9.37856C7.44345 9.41171 7.48848 9.43043 7.5355 9.43068Z" fill="white"/>
      </svg>
      ');
      background-size: cover;
      display: inline-flex;
      width: 18px;
      height: 18px;
      transform: translateY(15%);
    }
    .ant-radio-group:after {
      margin-left: -8px;
    }
    input,
    .ant-input-number,
    .ant-select-selector,
    .ant-picker,
    textarea {
      border-color: ${colors.warningColor} !important;
    }
    .ant-select-focused .ant-select-selector {
      box-shadow: 0 0 0 1px ${colors.warningColor} !important;
    }
    // File input
    .ant-upload-select-picture-card,
    .DocumentInputContentMobile {
      border-color: ${colors.warningColor} !important;
    }
    .ant-upload-list-item-name {
      color: ${colors.warningColor};
    }
    .ant-upload-list-picture-card .ant-upload-list-item {
      border: 1px solid ${colors.warningColor};
    }
    .ant-form-item-extra {
      font-size: 12px;
      color: ${colors.warningColor};
    }
  }
` as FormItemType;
